/**
 * Functions Tab
 *
 * Manage job functions (functies).
 * Note: Functions have no direct relation to niveau or weging.
 * Niveau is determined by employee's assigned tasks, not by function.
 */

import { useState } from 'react';
import { useFunctions, useDepartments, useCreateFunction, useUpdateFunction, useDeleteFunction, useRoleTypes } from '../useSettings';
import type { FunctionDTO, CreateFunctionRequest, UpdateFunctionRequest } from '@functieloonhuis/shared';

interface FunctionFormData {
  name: string;
  description: string;
  departmentId: number | null;
  // Epic 1 — Roltypes & niveau-bepaling v1, Story 1.2 (AC1).
  roleTypeId: number | null;
}

export function FunctionsTab() {
  const { data: functions, isLoading, error } = useFunctions();
  const { data: departments } = useDepartments();
  // Code-review fix (epic-level): includeInactive=true — the disabled
  // fallback option below needs the deactivated-but-still-linked role
  // type's actual NAME, which requires it to be present in this list at
  // all (RoleTypesTab.tsx already does this; FunctionsTab.tsx previously
  // didn't, so the fallback always rendered "Roltype <id> (gedeactiveerd)"
  // instead of the real name).
  const { data: roleTypes } = useRoleTypes(true);
  const createFunction = useCreateFunction();
  const updateFunction = useUpdateFunction();
  const deleteFunction = useDeleteFunction();

  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingFunction, setEditingFunction] = useState<FunctionDTO | null>(null);
  const [formData, setFormData] = useState<FunctionFormData>({
    name: '',
    description: '',
    departmentId: null,
    roleTypeId: null,
  });

  const resetForm = () => {
    setFormData({ name: '', description: '', departmentId: null, roleTypeId: null });
    setEditingFunction(null);
    setIsFormOpen(false);
  };

  const handleEdit = (func: FunctionDTO) => {
    setEditingFunction(func);
    setFormData({
      name: func.name,
      description: func.description || '',
      departmentId: func.departmentId,
      roleTypeId: func.roleTypeId,
    });
    setIsFormOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (editingFunction) {
      const updateData: UpdateFunctionRequest = {
        name: formData.name,
        description: formData.description || undefined,
        departmentId: formData.departmentId ?? undefined,
        // Always send roleTypeId explicitly (even when null) so the backend
        // can distinguish "unlink" from "field not sent" (AC4).
        roleTypeId: formData.roleTypeId,
      };
      await updateFunction.mutateAsync({ id: editingFunction.id, data: updateData });
    } else {
      const createData: CreateFunctionRequest = {
        name: formData.name,
        description: formData.description || undefined,
        departmentId: formData.departmentId ?? undefined,
      };
      await createFunction.mutateAsync(createData);
    }
    resetForm();
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Weet u zeker dat u deze functie wilt verwijderen?')) {
      await deleteFunction.mutateAsync(id);
    }
  };

  const handleToggleActive = async (func: FunctionDTO) => {
    await updateFunction.mutateAsync({
      id: func.id,
      data: { isActive: !func.isActive },
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-3 text-gray-600">Laden...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-sm text-red-700">Fout bij het laden van functies.</p>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-lg font-medium text-gray-900">Functies</h2>
          <p className="text-sm text-gray-500">Beheer functies en hun taaktoewijzingen</p>
        </div>
        <button
          onClick={() => setIsFormOpen(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
        >
          <svg className="mr-2 -ml-1 w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
          </svg>
          Nieuwe Functie
        </button>
      </div>

      {/* Form Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500/75 transition-opacity" onClick={resetForm}></div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="relative z-10 inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <form onSubmit={handleSubmit}>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  {editingFunction ? 'Functie Bewerken' : 'Nieuwe Functie'}
                </h3>

                <div className="space-y-4">
                  {/* Name */}
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                      Naam
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                      required
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label htmlFor="description" className="block text-sm font-medium text-gray-700">
                      Beschrijving
                    </label>
                    <textarea
                      id="description"
                      rows={2}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    />
                  </div>

                  {/* Team */}
                  <div>
                    <label htmlFor="departmentId" className="block text-sm font-medium text-gray-700">
                      Team
                    </label>
                    <select
                      id="departmentId"
                      value={formData.departmentId ?? ''}
                      onChange={(e) => setFormData({ ...formData, departmentId: e.target.value ? Number(e.target.value) : null })}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm"
                    >
                      <option value="">Geen team</option>
                      {departments?.filter(d => d.isActive).map((dept) => (
                        <option key={dept.id} value={dept.id}>
                          {dept.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Role Type (Epic 1 — Roltypes & niveau-bepaling v1, Story 1.2) */}
                  {editingFunction && (
                    <div>
                      <label htmlFor="roleTypeId" className="block text-sm font-medium text-gray-700">
                        Roltype
                      </label>
                      <select
                        id="roleTypeId"
                        data-testid="function-role-type-select"
                        value={formData.roleTypeId ?? ''}
                        onChange={(e) => setFormData({ ...formData, roleTypeId: e.target.value ? Number(e.target.value) : null })}
                        className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-[#54949E] focus:ring-[#54949E] sm:text-sm"
                      >
                        <option value="">Geen roltype</option>
                        {roleTypes?.filter(rt => rt.isActive).map((roleType) => (
                          <option key={roleType.id} value={roleType.id}>
                            {roleType.name}
                          </option>
                        ))}
                        {/* Code-review fix: als de huidige koppeling naar een inmiddels
                            gedeactiveerd roltype wijst (toegestaan, AC5 — "blijft van
                            kracht"), toon 'm alsnog als disabled optie i.p.v. de select
                            stil op "Geen roltype" te laten terugvallen terwijl de
                            koppeling in werkelijkheid nog bestaat. */}
                        {formData.roleTypeId !== null &&
                          !roleTypes?.some((rt) => rt.isActive && rt.id === formData.roleTypeId) && (
                            <option value={formData.roleTypeId} disabled>
                              {roleTypes?.find((rt) => rt.id === formData.roleTypeId)?.name ?? `Roltype ${formData.roleTypeId}`}
                              {' '}(gedeactiveerd)
                            </option>
                          )}
                      </select>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={resetForm}
                    className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
                  >
                    Annuleren
                  </button>
                  <button
                    type="submit"
                    disabled={createFunction.isPending || updateFunction.isPending}
                    className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                  >
                    {createFunction.isPending || updateFunction.isPending ? 'Opslaan...' : 'Opslaan'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Functions List */}
      {functions && functions.length > 0 ? (
        <div className="overflow-hidden border border-gray-200 rounded-lg">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Functie
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Team
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Roltype
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Medewerkers
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acties
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {functions.map((func) => (
                <tr key={func.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{func.name}</div>
                      {func.description && (
                        <div className="text-sm text-gray-500 truncate max-w-xs">{func.description}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {func.departmentName || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {func.roleTypeName ? (
                      func.roleTypeName
                    ) : (
                      // AC3: neutrale weergave zonder waarschuwingskleur/-icoon.
                      <span className="text-gray-400">Geen roltype gekoppeld</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {func.employeesCount ?? 0}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleActive(func)}
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium cursor-pointer ${
                        func.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}
                    >
                      {func.isActive ? 'Actief' : 'Inactief'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <button
                      onClick={() => handleEdit(func)}
                      className="text-blue-600 hover:text-blue-900 mr-4"
                    >
                      Bewerken
                    </button>
                    <button
                      onClick={() => handleDelete(func.id)}
                      className="text-red-600 hover:text-red-900"
                      disabled={deleteFunction.isPending}
                    >
                      Verwijderen
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-12">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <h3 className="mt-4 text-lg font-medium text-gray-900">Geen functies gevonden</h3>
          <p className="mt-2 text-sm text-gray-500">Begin met het toevoegen van functies.</p>
        </div>
      )}
    </div>
  );
}
